import axios from 'axios';

type PortalLanguage = 'ar' | 'en';

type ValidationIssue = {
  type?: string;
  loc?: Array<string | number>;
  msg?: string;
  ctx?: Record<string, unknown>;
};

const fieldLabels: Record<string, { ar: string; en: string }> = {
  country_id: { ar: 'الدولة', en: 'Country' },
  program_id: { ar: 'الخدمة', en: 'Service' },
  answers: { ar: 'إجابات التسعير', en: 'Pricing answers' },
  nationality: { ar: 'الجنسية', en: 'Nationality' },
  national_id: { ar: 'رقم الهوية', en: 'National ID' },
  passport_number: { ar: 'رقم جواز السفر', en: 'Passport number' },
  address: { ar: 'العنوان', en: 'Address' },
  phone: { ar: 'رقم الهاتف', en: 'Phone number' },
  guardian_name: { ar: 'اسم ولي الأمر', en: 'Guardian name' },
  guardian_national_id: { ar: 'رقم هوية ولي الأمر', en: 'Guardian ID' },
  guardian_phone: { ar: 'هاتف ولي الأمر', en: 'Guardian phone' },
  qualification: { ar: 'المؤهل الدراسي', en: 'Qualification' },
  desired_specialization: { ar: 'بيانات العقد', en: 'Contract details' },
  study_level: { ar: 'المرحلة المطلوبة', en: 'Study level' },
  file_notes: { ar: 'ملاحظات الملف', en: 'File notes' },
  first_name: { ar: 'الاسم الأول', en: 'First name' },
  last_name: { ar: 'اسم العائلة', en: 'Last name' },
  reference: { ar: 'رقم العملية', en: 'Transaction reference' },
  proof_file_url: { ar: 'إثبات الدفع', en: 'Payment proof' },
};

const validationMessage = (issue: ValidationIssue, language: PortalLanguage) => {
  const key = [...(issue.loc || [])].reverse().find((part) => typeof part === 'string' && part !== 'body');
  const field = typeof key === 'string'
    ? (fieldLabels[key]?.[language] || (language === 'ar' ? 'هذا الحقل' : key.replaceAll('_', ' ')))
    : (language === 'ar' ? 'البيانات' : 'Data');
  const min = Number(issue.ctx?.min_length || 0);
  const max = Number(issue.ctx?.max_length || 0);

  if (issue.type === 'missing') return language === 'ar' ? `${field}: هذا الحقل مطلوب.` : `${field}: this field is required.`;
  if (issue.type === 'string_too_short') return language === 'ar'
    ? `${field}: أدخل ${min || 2} أحرف على الأقل.`
    : `${field}: enter at least ${min || 2} characters.`;
  if (issue.type === 'string_too_long') return language === 'ar'
    ? `${field}: الحد الأقصى ${max || ''} حرفًا.`
    : `${field}: use no more than ${max || ''} characters.`;
  if (issue.type === 'value_error') return language === 'ar' ? `${field}: القيمة المدخلة غير صحيحة.` : `${field}: the entered value is invalid.`;
  return language === 'ar' ? `${field}: راجع القيمة المدخلة.` : `${field}: please review this value.`;
};

export function portalApiError(
  error: unknown,
  language: PortalLanguage,
  fallback?: { ar: string; en: string },
) {
  const fallbackMessage = fallback?.[language] || (language === 'ar' ? 'تعذر إكمال الإجراء. حاول مرة أخرى.' : 'We could not complete this action. Please try again.');
  if (!axios.isAxiosError(error)) return fallbackMessage;

  if (!error.response) {
    return language === 'ar'
      ? 'تعذر الاتصال بالخادم. تحقق من الإنترنت ثم حاول مرة أخرى، ولن تفقد البيانات التي أدخلتها.'
      : 'We could not reach the server. Check your connection and try again; your entered data is still here.';
  }

  const { status, data } = error.response;
  const detail = data?.detail;
  if (Array.isArray(detail)) {
    const messages = [...new Set(detail.map((issue: ValidationIssue) => validationMessage(issue, language)))];
    return messages.slice(0, 3).join(' ');
  }
  if (detail?.code === 'COUNTRY_LOCKED_AFTER_CONTRACT' || detail?.code === 'COUNTRY_LOCKED_AFTER_CONTRACT_REVIEW') {
    return language === 'ar'
      ? 'تم تثبيت الدولة بعد مراجعة العقد. تواصل مع الدعم إذا احتجت إلى تعديل استثنائي.'
      : 'Your destination was locked after contract review. Contact support if an exceptional change is required.';
  }
  if (status === 401) return language === 'ar' ? 'انتهت جلسة الدخول. سجّل الدخول مرة أخرى للمتابعة.' : 'Your session has expired. Sign in again to continue.';
  if (status === 403) return language === 'ar' ? 'هذه الخطوة غير متاحة لحسابك حاليًا.' : 'This step is not available for your account yet.';
  if (status === 404) return language === 'ar' ? 'لم تعد هذه البيانات متاحة. حدّث الصفحة ثم حاول مرة أخرى.' : 'This information is no longer available. Refresh and try again.';
  if (status === 409) return language === 'ar' ? 'تم تحديث هذه البيانات في مكان آخر. حدّث الصفحة قبل المتابعة.' : 'This information changed elsewhere. Refresh before continuing.';
  if (status === 413) return language === 'ar' ? 'حجم الملف أكبر من المسموح. اختر ملفًا أصغر ثم حاول مرة أخرى.' : 'This file is too large. Choose a smaller file and try again.';
  if (status >= 500) return language === 'ar' ? 'الخدمة غير متاحة مؤقتًا. بياناتك محفوظة؛ حاول بعد قليل.' : 'The service is temporarily unavailable. Your data is safe; try again shortly.';
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail.message === 'string' && detail.message.trim()) return detail.message;
  return fallbackMessage;
}
